import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { FSAWithMeta } from '@makemydeal/dr-browser-utils';
import { getDiffPropsFromObjects } from '@makemydeal/dr-common-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { SocketChangedBy } from '@makemydeal/dr-shared-bff-types';
import { getEcommerceOrderReferences } from '@makemydeal/dr-shared-types';

import { SOCKET_EVENT_IGNORED, SOCKET_EVENT_RECEIVED, UPDATE_OMS_ORDER_NUMBER } from '../../actionTypes/socketActionTypes';
import {
    APP_NAME_BLACKLIST,
    DIGITAL_CONTRACTING_APP_NAME,
    IGNORED_CONTRACTING_PATCH_TYPES,
    PERSONA_WHITE_LIST,
    VIN_CRM_APPLICATION_NAME,
    COMMON_CONSUMER_APPLICATION_NAME
} from '../../constants';
import { getCreditAppReferenceId, getIsParticipantsSaveInFlight } from '../../selectors/creditAppSelectors';
import { getEcommerceOrderNumber, getOrderDetailsLink, getTenantId } from '../../selectors/dealXgDetailsSelectors';
import { isSelectedLenderPaperLender } from '../../selectors/lenderListSelectors';
import { getDealLifecycle } from '../../selectors/dealLifecycleSelectors';
import * as offerReduxSelectors from '../../selectors/offerRedux';
import { getVehicleInsuranceInfo } from '../../selectors/BaseDealSelectors';
import { createSocketLoggingContext } from './loggingContext';
import { isDealVersionFromDarwin } from '../../utils/darwinEventUtils';
import type { EventReceived } from './types';

interface UpdateOmsOrderNumberPayload {
    omsOrderNumber?: string;
    tenantId?: string;
    orderDetailsLink?: string;
}

/**
 * Checks if OMS order number, tenantId, or orderDetailsLink have changed and returns appropriate action
 * Updates all three values in state to support cancel order functionality
 */
const checkOmsOrderNumberChange = (
    state: StateTree,
    event: any
): FSAWithMeta<typeof UPDATE_OMS_ORDER_NUMBER, UpdateOmsOrderNumberPayload, { sourceEvent: any }> | null => {
    const {
        orderNumber: newOmsOrderNumber,
        tenantId: newTenantId,
        orderLink: newOrderDetailsLink
    } = getEcommerceOrderReferences(event.data);

    const currentOmsOrderNumber = getEcommerceOrderNumber(state);
    const currentTenantId = getTenantId(state);
    const currentOrderDetailsLink = getOrderDetailsLink(state);

    const loggingContext = createSocketLoggingContext(state, event);

    // Check if any of the three values have changed
    const hasOmsOrderNumberChanged = newOmsOrderNumber && currentOmsOrderNumber !== newOmsOrderNumber;
    const hasTenantIdChanged = newTenantId && currentTenantId !== newTenantId;
    const hasOrderDetailsLinkChanged = newOrderDetailsLink && currentOrderDetailsLink !== newOrderDetailsLink;

    // If none of the values have changed or are present, return null
    if (!hasOmsOrderNumberChanged && !hasTenantIdChanged && !hasOrderDetailsLinkChanged) {
        if (!newOmsOrderNumber && !newTenantId && !newOrderDetailsLink) {
            logNewRelicPageAction('MV:Socket - E-commerce Data Missing', {
                ...loggingContext,
                reason: 'no_ecommerce_data_in_event',
                hasCurrentOmsOrderNumber: Boolean(currentOmsOrderNumber),
                hasCurrentTenantId: Boolean(currentTenantId),
                hasCurrentOrderDetailsLink: Boolean(currentOrderDetailsLink)
            });
        } else {
            logNewRelicPageAction('MV:Socket - E-commerce Data Unchanged', {
                ...loggingContext,
                reason: 'ecommerce_data_same',
                currentOmsOrderNumber,
                currentTenantId,
                currentOrderDetailsLink,
                newOmsOrderNumber,
                newTenantId,
                newOrderDetailsLink
            });
        }
        return null;
    }

    // Log successful e-commerce data update
    logNewRelicPageAction('MV:Socket - E-commerce Data Updated', {
        ...loggingContext,
        currentOmsOrderNumber,
        currentTenantId,
        currentOrderDetailsLink,
        newOmsOrderNumber,
        newTenantId,
        newOrderDetailsLink,
        isFirstTimeSet: !currentOmsOrderNumber && !currentTenantId && !currentOrderDetailsLink,
        reason: 'ecommerce_data_changed',
        omsOrderNumberChanged: hasOmsOrderNumberChanged,
        tenantIdChanged: hasTenantIdChanged,
        orderDetailsLinkChanged: hasOrderDetailsLinkChanged
    });

    // Dispatch action to update all three values in dealXgDetails state
    // Only include values that are present (not undefined)
    const payload: UpdateOmsOrderNumberPayload = {};
    if (newOmsOrderNumber) payload.omsOrderNumber = newOmsOrderNumber;
    if (newTenantId) payload.tenantId = newTenantId;
    if (newOrderDetailsLink) payload.orderDetailsLink = newOrderDetailsLink;

    return {
        type: UPDATE_OMS_ORDER_NUMBER,
        payload,
        // This return short-circuits the SOCKET_EVENT_RECEIVED / SOCKET_EVENT_IGNORED decision below,
        // so any middleware keyed on those types never observes the frame. The frame rides on meta
        // (reducers read payload only) so consumers that need it — commonConsumerIdMiddleware reads
        // the buyer's commonConsumerId off it — are not blinded by an unrelated e-commerce change.
        meta: { sourceEvent: event }
    };
};

const checkInsuranceDataChange = (state: StateTree, event: any): boolean => {
    const newVehicleInsuranceInfo = event.data?.vehicleInsurance;
    const currentVehicleInsuranceInfo = getVehicleInsuranceInfo(state);

    if (!currentVehicleInsuranceInfo && newVehicleInsuranceInfo) {
        return true;
    }

    const diffProps = getDiffPropsFromObjects(newVehicleInsuranceInfo, currentVehicleInsuranceInfo);

    return diffProps.length > 0;
};

const shouldAutoRefreshOnContractSigned = (state: StateTree, event: any, dealId: string): boolean => {
    const currentContractSignedDate = getDealLifecycle(state)?.contractSignedDate;
    const newContractSignedDate = event.data?.dealLifecycle?.contractSignedDate;
    const isContractSignedDateUpdated = newContractSignedDate && currentContractSignedDate !== newContractSignedDate;
    logNewRelicPageAction('MV:WebSocket', {
        type: 'contractSigned',
        dealId,
        meta: { currentContractSignedDate, newContractSignedDate }
    });

    return isContractSignedDateUpdated;
};

/**
 * Catch-all path for any event no topic-specific handler claimed. Decides between
 * SOCKET_EVENT_RECEIVED (with attached metadata flags) and SOCKET_EVENT_IGNORED based on
 * persona whitelist, app-name blacklist, and several deal-level change checks.
 */
export const handleGenericPath = (state: StateTree, event: any): EventReceived => {
    const enableVinCrmPersonaFallback = featureToggleSelectors.enableVinCrmPersonaFallback(state);
    const enableCommonConsumerPersonaFallback = featureToggleSelectors.enableCommonConsumerPersonaFallback(state);
    const changedBy: SocketChangedBy | undefined = event.data?.changedBy;
    const versionCreatedBy: SocketChangedBy | undefined = event.data?.dealStructure?.versionCreatedBy;

    const patchTypes = event.data?.patchTypes ?? [];
    const isContractingDocPatch =
        changedBy?.applicationName?.toLowerCase() === DIGITAL_CONTRACTING_APP_NAME &&
        patchTypes.some((patchType: string) => IGNORED_CONTRACTING_PATCH_TYPES.includes(patchType));
    if (isContractingDocPatch) {
        return { type: SOCKET_EVENT_IGNORED, payload: event };
    }

    const isVinCrmSourced =
        enableVinCrmPersonaFallback && versionCreatedBy?.applicationName?.toLowerCase() === VIN_CRM_APPLICATION_NAME;

    const isCommonConsumerSourced =
        enableCommonConsumerPersonaFallback &&
        versionCreatedBy?.applicationName?.toLowerCase() === COMMON_CONSUMER_APPLICATION_NAME;

    // DXG schema states changedBy is for global updates, versionCreatedBy is for create ops
    const resolvedChangedBy: SocketChangedBy | undefined =
        isVinCrmSourced || isCommonConsumerSourced || event.topic === 'dealXgCreate'
            ? {
                  applicationName: versionCreatedBy?.applicationName ?? changedBy?.applicationName,
                  persona: versionCreatedBy?.persona ?? changedBy?.persona
              }
            : changedBy;

    // App-name blacklist short-circuit.
    if (featureToggleSelectors.enableNotificationSuppress(state)) {
        const isAppNameBlackListed: boolean = APP_NAME_BLACKLIST.includes(resolvedChangedBy?.applicationName?.toLowerCase() ?? '');
        if (isAppNameBlackListed) {
            return { type: SOCKET_EVENT_IGNORED, payload: event };
        }
    }

    const personaValidated = PERSONA_WHITE_LIST.includes(resolvedChangedBy?.persona?.toLowerCase() ?? '');
    const isSocketEventFromDarwin = event.topic === 'dealXgCreate' && isDealVersionFromDarwin(event);

    const dealId = offerReduxSelectors.getDealXgId(state);
    if (isSocketEventFromDarwin) {
        logNewRelicPageAction('MV:WebSocket', { type: 'darwin', dealId });
    }

    const currentCreditAppId = getCreditAppReferenceId(state);
    const newCreditAppId = event.data?.dealStructure?.creditApplication?.creditApplicationId;
    const isCreditAppUpdated = newCreditAppId && currentCreditAppId !== newCreditAppId && !getIsParticipantsSaveInFlight(state);
    if (isCreditAppUpdated) {
        logNewRelicPageAction('MV:WebSocket', { type: 'creditAppIdUpdated', dealId, meta: { currentCreditAppId, newCreditAppId } });
    }

    const isContractSignedAutoRefreshEnabled = shouldAutoRefreshOnContractSigned(state, event, dealId);
    const isPaperLenderFeatureEnabled = featureToggleSelectors.isPaperLenderEnabled(state);
    const isPaperLender = isSelectedLenderPaperLender(state);
    const isVehicleInsuranceUpdated = checkInsuranceDataChange(state, event);
    const isSocketEventFromNonCMD =
        event.topic === 'dealXgCreate' && event.data?.dealStructure?.versionCreatedBy?.applicationName !== 'CMD';

    const omsChangeAction = checkOmsOrderNumberChange(state, event);
    if (omsChangeAction) return omsChangeAction;

    if (
        personaValidated ||
        (isVehicleInsuranceUpdated && isSocketEventFromNonCMD) ||
        isSocketEventFromDarwin ||
        isCreditAppUpdated ||
        isContractSignedAutoRefreshEnabled
    ) {
        const activityMilestones = state.activityMilestones;
        const updatedMilestones = getDiffPropsFromObjects(event.data.dealProgressMilestones, activityMilestones);
        event.updatedMilestones = updatedMilestones;
        event.hasContractSignDateUpdated = isContractSignedAutoRefreshEnabled && !(isPaperLenderFeatureEnabled && isPaperLender);
        event.isVehicleInsuranceUpdated = isVehicleInsuranceUpdated;
        event.isCommonConsumerSourced = isCommonConsumerSourced;

        return {
            type: SOCKET_EVENT_RECEIVED,
            payload: { ...event, data: { ...event.data, changedBy: resolvedChangedBy } }
        };
    }

    logNewRelicPageAction('MV:WebSocket', {
        type: 'socketEventIgnored',
        persona: resolvedChangedBy?.persona ?? '',
        applicationName: resolvedChangedBy?.applicationName ?? '',
        hasDealProgressMilestones: !!(
            event.data?.dealProgressMilestones && Object.keys(event.data.dealProgressMilestones).length > 0
        )
    });
    return { type: SOCKET_EVENT_IGNORED, payload: event };
};
